import dynamic from 'next/dynamic';
import React from 'react';

// components
import { Box, Section } from 'src/components-v2/Layout';
import Footer from 'src/components-v3/Footer';
import { ContentfulToolbar } from 'src/components/ContentfulToolbar';
import { UnidentifiedTraffic } from 'src/components/Feedback';
import { ContactRoutingJumpLinks } from 'src/components-v3/ContactRouting/ContactRoutingJumpLinks';
import { MetaTags } from 'src/components/MetaTags';
import { ConsentManager } from 'src/containers/ConsentManager';
import { CmsThemeContextProvider } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';
import { BotCheckEvent } from 'src/hooks/useBotCheckEvent';
import { PerformanceProvider } from 'src/hooks/useIsPotato';

// helpers
import { AnalyticsProvider, LocaleContext } from 'src/contexts';
import { CmsThemes, Locales, type TLocale } from 'src/lib/constants';
import type { FooterVariant } from 'src/lib/site-footer';
import type { NavigationVariant } from 'src/lib/site-navigation';
import type { Sys } from 'src/types/contentful-sdk-compat';
import type SxProps from 'threads5/themes/sx-props';
import type { ITheme } from 'threads5/themes/theme';

// scripts
import SiteNavigation from 'src/components-v3/SiteNavigation/SiteNavigation';
import { MainTemplateContextProvider } from 'src/contexts/MainTemplateContext/MainTemplateContext';

const ForeignContentNotification = dynamic(
  () => {
    return import(
      'src/components/Feedback/ForeignContentNotification/ForeignContentNotification'
    );
  },
  {
    ssr: false,
  },
);

// For both, new static and new CMS pages (using in Page template)
const MainTemplate = ({
  children,
  locale = Locales.EN_US,
  hasForeignContentNotification = true,
  hasFooter = true,
  footerVariant = 'default',
  hasNavigation = true,
  navigationVariant = 'default',
  cmsTheme = CmsThemes.WEB2,
  sx = {},
  sys,
  ...props
}: {
  children: React.ReactNode;
  locale?: TLocale;
  hasForeignContentNotification?: boolean;
  hasFooter?: boolean;
  footerVariant?: FooterVariant;
  hasNavigation?: boolean;
  navigationVariant?: NavigationVariant;
  cmsTheme?: string;
  sx?: SxProps<ITheme>;
  sys?: Sys;
  [K: string]: unknown;
}) => {
  /*
   * NOTE
   * ---------------------------------------------------------------------------
   * Moving forward all context providers should be added to the _app.tsx file.
   * This is to ensure that the context providers are available for the entire
   * application and not just the current page.
   *
   */
  return (
    <MainTemplateContextProvider>
      <CmsThemeContextProvider cmsTheme={cmsTheme}>
        <LocaleContext.Provider value={locale || Locales.EN_US}>
          <AnalyticsProvider>
            <BotCheckEvent />
            <PerformanceProvider>
              {/* @ts-ignore */}
              <MetaTags {...props} />
              <Box
                sx={{
                  bgcolor: 'white',
                  ...sx,
                }}
              >
                {hasNavigation && (
                  <>
                    <SiteNavigation
                      locale={locale}
                      variant={navigationVariant}
                    />
                    {hasForeignContentNotification && (
                      <ForeignContentNotification hasFloatingNav />
                    )}
                  </>
                )}

                <Section
                  component='main'
                  id='main-content'
                  data-contentful-page-id={sys?.id} // manually added for contentful inspection in dev menu
                >
                  {children}
                </Section>
              </Box>
              {hasFooter && <Footer variant={footerVariant} />}
              <ConsentManager />
              <UnidentifiedTraffic />
              <ContactRoutingJumpLinks />
              <ContentfulToolbar />
            </PerformanceProvider>
          </AnalyticsProvider>
        </LocaleContext.Provider>
      </CmsThemeContextProvider>
    </MainTemplateContextProvider>
  );
};

export default MainTemplate;
